import { useMutation, useQuery, useQueryClient } from 'react-query'
import axios, { AxiosError } from 'axios'
import chunk from 'lodash/chunk'

import {
  CourtReporterExhibit,
  CourtReporterExhibitSubmission,
  CourtReporterExhibitUploadUrls,
  EditCourtReporterExhibitPayload,
  NewCourtReporterExhibit,
} from '@/apiClient'
import { exhibitMimeType } from '@/components/Event/CourtReporterDetail/exhibits'
import { uploadPresignedS3Post } from '@/utils/s3'

export type ExhibitSubmissionEvent = { exhibit_submission_url: string }

type ApiError = AxiosError<{ detail?: string }>

export type ExhibitFields = Omit<NewCourtReporterExhibit, 'key' | 'filename'>

// Parrot presigns at most this many exhibit files in one request.
const MAX_FILES_PER_PRESIGN = 50

// Enough to keep the connection busy without starving each file of bandwidth.
const UPLOAD_CONCURRENCY = 3

const exhibitSubmissionQueryKey = (event: ExhibitSubmissionEvent) => [
  'court_reporter_exhibit_submission',
  event.exhibit_submission_url,
]

export const useExhibitSubmissionQuery = (event: ExhibitSubmissionEvent) =>
  useQuery<CourtReporterExhibitSubmission, AxiosError>(
    exhibitSubmissionQueryKey(event),
    () =>
      axios
        .get<CourtReporterExhibitSubmission>(event.exhibit_submission_url)
        .then(r => r.data),
  )

const uploadExhibitFiles = async (
  event: ExhibitSubmissionEvent,
  files: { file: File; exhibitId?: string }[],
  onProgress?: (index: number, progress: number) => void,
): Promise<string[]> => {
  const keys: string[] = []
  for (const batch of chunk(files, MAX_FILES_PER_PRESIGN)) {
    const { data } = await axios.post<CourtReporterExhibitUploadUrls>(
      `${event.exhibit_submission_url}/upload-url`,
      {
        files: batch.map(({ file, exhibitId }) => ({
          mime_type: exhibitMimeType(file),
          exhibit_id: exhibitId,
        })),
      },
    )
    const offset = keys.length
    let next = 0
    const uploadRest = async () => {
      while (next < data.uploads.length) {
        const i = next++
        await uploadPresignedS3Post({
          file: batch[i].file,
          presignedPost: data.uploads[i].presign_data,
          onProgress: progress => onProgress?.(offset + i, progress),
        })
      }
    }
    await Promise.all(
      Array.from(
        { length: Math.min(UPLOAD_CONCURRENCY, data.uploads.length) },
        uploadRest,
      ),
    )
    keys.push(...data.uploads.map(upload => upload.key))
  }
  return keys
}

export type NewExhibit = { fields: ExhibitFields; file?: File }

export const useAddExhibitsMutation = (event: ExhibitSubmissionEvent) => {
  const queryClient = useQueryClient()

  return useMutation<
    CourtReporterExhibit[],
    ApiError,
    {
      exhibits: NewExhibit[]
      onProgress?: (exhibit: NewExhibit, progress: number) => void
    }
  >(
    async ({ exhibits, onProgress }) => {
      const withFiles = exhibits.filter(e => e.file !== undefined)
      const keys = await uploadExhibitFiles(
        event,
        withFiles.map(e => ({ file: e.file as File })),
        (index, progress) => onProgress?.(withFiles[index], progress),
      )
      const keyOf = new Map(withFiles.map((e, i) => [e, keys[i]]))

      const { data } = await axios.post<CourtReporterExhibit[]>(
        `${event.exhibit_submission_url}/exhibits`,
        {
          exhibits: exhibits.map(e => ({
            ...e.fields,
            key: keyOf.get(e),
            filename: e.file?.name,
          })),
        },
      )
      return data
    },
    {
      onSettled: () =>
        queryClient.invalidateQueries(exhibitSubmissionQueryKey(event)),
    },
  )
}

export const useEditExhibitMutation = (event: ExhibitSubmissionEvent) => {
  const queryClient = useQueryClient()

  return useMutation<
    CourtReporterExhibit,
    ApiError,
    {
      exhibit: CourtReporterExhibit
      changes: EditCourtReporterExhibitPayload
      file?: File
    }
  >(
    async ({ exhibit, changes, file }) => {
      const fileChanges = file
        ? {
            key: (
              await uploadExhibitFiles(event, [{ file, exhibitId: exhibit.id }])
            )[0],
            filename: file.name,
          }
        : {}

      const { data } = await axios.patch<CourtReporterExhibit>(
        `${event.exhibit_submission_url}/exhibits/${exhibit.id}`,
        { ...changes, ...fileChanges },
      )
      return data
    },
    {
      onSettled: () =>
        queryClient.invalidateQueries(exhibitSubmissionQueryKey(event)),
    },
  )
}

export const useMoveExhibitMutation = (event: ExhibitSubmissionEvent) => {
  const queryClient = useQueryClient()
  const queryKey = exhibitSubmissionQueryKey(event)

  return useMutation<
    CourtReporterExhibit,
    ApiError,
    { exhibit: CourtReporterExhibit; to: number },
    { previous?: CourtReporterExhibitSubmission }
  >(
    ({ exhibit, to }) =>
      axios
        .patch<CourtReporterExhibit>(
          `${event.exhibit_submission_url}/exhibits/${exhibit.id}`,
          { index: to },
        )
        .then(r => r.data),
    {
      // The list moves on drop rather than once Parrot has renumbered it.
      onMutate: async ({ exhibit, to }) => {
        await queryClient.cancelQueries(queryKey)
        const previous =
          queryClient.getQueryData<CourtReporterExhibitSubmission>(queryKey)
        if (previous) {
          const exhibits = previous.exhibits.filter(e => e.id !== exhibit.id)
          exhibits.splice(to, 0, exhibit)
          queryClient.setQueryData(queryKey, {
            ...previous,
            exhibits: exhibits.map((e, index) => ({ ...e, index })),
          })
        }
        return { previous }
      },
      onError: (_error, _variables, context) => {
        if (context?.previous) {
          queryClient.setQueryData(queryKey, context.previous)
        }
      },
      onSettled: () => queryClient.invalidateQueries(queryKey),
    },
  )
}

export const useRemoveExhibitMutation = (event: ExhibitSubmissionEvent) => {
  const queryClient = useQueryClient()

  return useMutation<void, ApiError, CourtReporterExhibit>(
    exhibit =>
      axios
        .delete(`${event.exhibit_submission_url}/exhibits/${exhibit.id}`)
        .then(() => undefined),
    {
      onSettled: () =>
        queryClient.invalidateQueries(exhibitSubmissionQueryKey(event)),
    },
  )
}

export const useSubmitExhibitsMutation = (event: ExhibitSubmissionEvent) => {
  const queryClient = useQueryClient()

  return useMutation<CourtReporterExhibitSubmission, ApiError>(
    () =>
      axios
        .post<CourtReporterExhibitSubmission>(
          `${event.exhibit_submission_url}/submit`,
        )
        .then(r => r.data),
    {
      onSuccess: data => {
        queryClient.setQueryData(exhibitSubmissionQueryKey(event), data)
      },
      onSettled: () =>
        queryClient.invalidateQueries(exhibitSubmissionQueryKey(event)),
    },
  )
}
